import { useCallback, useEffect, useRef, useState } from 'react';
import { read as xlsxRead, utils as xlsxUtils } from 'xlsx';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Modal from '../../components/Modal';
import Icon from '../../components/Icon';
import { Banner, TextArea } from '../../components/ui';
import type { CustomerFormApp, CustomerImportResult } from '../../types';

/**
 * Bringing in a customer list.
 *
 * Built on the same bet as the product import: an admin's shops already live
 * in a spreadsheet, so paste beats upload and sidesteps every question about
 * encodings and sheets. Unlike products the columns are named, not
 * positional — a customer form has arbitrary custom fields — so the first
 * row is the header and each row becomes an object keyed by it.
 *
 * The preview is load-bearing. A bad import is hundreds of wrong shops with
 * no undo, so nothing is written until the admin has seen the counts, the
 * areas that would be created, and exactly which rows fail and why.
 */
export default function CustomerImport({
  onClose, onImported,
}: { onClose: () => void; onImported: () => void }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<CustomerImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [customColumns, setCustomColumns] = useState<string[]>([]);

  // Best-effort: show the form's own custom fields in the help, so the admin
  // knows exactly which extra columns this company's import understands.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const app = await api.get<CustomerFormApp>('/customers/form/app');
        if (!cancelled) {
          setCustomColumns(app.fields.filter((f) => !f.system).map((f) => f.label || f.key));
        }
      } catch {
        // The paste box works without the hint.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');

  const handleFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target?.result as ArrayBuffer);
        const wb = xlsxRead(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]!]!;
        const tsv: string = xlsxUtils.sheet_to_csv(ws, { FS: '	' });
        setText(tsv);
        setPreview(null);
      } catch {
        toast.error('Could not read that file. Try .xlsx, .xls, or .csv.');
      }
    };
    reader.readAsArrayBuffer(file);
    // Reset so the same file can be re-selected
    e.target.value = '';
  }, [toast]);

  const rows = parse(text);

  async function run(dryRun: boolean) {
    if (!rows.length) return;
    setBusy(true);
    try {
      const result = await api.post<CustomerImportResult>('/customers/import', {
        rows, dryRun,
      });
      if (dryRun) {
        setPreview(result);
      } else {
        toast.success(
          `${result.created ?? 0} added`
          + (result.skipped ? `, ${result.skipped} skipped` : '')
          + (result.failed.length ? `, ${result.failed.length} failed.` : '.'),
        );
        onImported();
      }
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not import that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Import customers"
      subtitle="Upload an Excel file or paste from a spreadsheet. New areas are created automatically."
      wide
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          {preview ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || (preview.willCreate ?? 0) === 0}
              onClick={() => void run(false)}
            >
              {busy ? <span className="spinner" /> : null}
              Import {preview.willCreate ?? 0} customer{(preview.willCreate ?? 0) === 1 ? '' : 's'}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || !rows.length}
              onClick={() => void run(true)}
            >
              {busy ? <span className="spinner" /> : null}
              Check {rows.length || ''} rows
            </button>
          )}
        </>
      )}
    >
      <div className="col gap-12">
        <Banner kind="info">
          <strong>Columns (the first row is the header):</strong>{' '}
          <span className="mono small">
            name*, area*, type, code, latitude, longitude
            {customColumns.length ? `, ${customColumns.join(', ')}` : ''}
          </span>
          <div style={{ marginTop: 4 }}>
            Only <strong>name</strong> and <strong>area</strong> are required. An area
            that does not exist yet is created. A <strong>type</strong> that is not
            recognised is left blank. Tabs (straight from Excel) or commas both work.
          </div>
        </Banner>

        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ whiteSpace: "nowrap" }}
            onClick={() => fileRef.current?.click()}
          >
            <Icon name="upload" size={16} />
            {" "}Upload Excel / CSV
          </button>
          {fileName ? (
            <span className="small">{fileName}</span>
          ) : (
            <span className="small faint">or paste rows below</span>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={handleFile}
          />
        </div>

        <TextArea
          label="Paste your rows"
          rows={8}
          className="mono"
          placeholder={'name\tarea\ttype\ncorner store\tandheri west\tretail'}
          value={text}
          onChange={(e) => { setText(e.target.value); setPreview(null); setFileName(''); }}
        />

        {rows.length > 0 && !preview ? (
          <div className="small faint">
            {rows.length} row{rows.length === 1 ? '' : 's'} read. Nothing is saved until
            you have seen what it would do.
          </div>
        ) : null}

        {preview ? (
          <>
            <div className="stat-row">
              <div className="stat">
                <div className="stat-icon"><Icon name="plus" size={18} /></div>
                <div>
                  <div className="stat-label">New</div>
                  <div className="stat-value">{preview.willCreate ?? 0}</div>
                </div>
              </div>
              <div className="stat">
                <div className="stat-icon"><Icon name="x" size={18} /></div>
                <div>
                  <div className="stat-label">Skipped</div>
                  <div className="stat-value">{preview.willSkip ?? 0}</div>
                </div>
              </div>
              <div className="stat">
                <div className="stat-icon"><Icon name="alert" size={18} /></div>
                <div>
                  <div className="stat-label">Failed</div>
                  <div className={`stat-value${preview.failed.length ? ' danger' : ''}`}>
                    {preview.failed.length}
                  </div>
                </div>
              </div>
            </div>

            {preview.newAreas?.length ? (
              <Banner kind="info">
                <strong>These areas will be created:</strong>{' '}
                {preview.newAreas.join(', ')}
              </Banner>
            ) : null}

            {preview.unknownTypes?.length ? (
              <Banner kind="warning">
                <strong>Unrecognised types, left blank:</strong>{' '}
                {preview.unknownTypes.join(', ')}
              </Banner>
            ) : null}

            {preview.newFields?.length ? (
              <Banner kind="info">
                <strong>New fields will be added to the form:</strong>{' '}
                {preview.newFields.join(', ')}
              </Banner>
            ) : null}

            {preview.failed.length ? (
              <div className="card">
                <div className="card-head">
                  <h3>These rows will be skipped</h3>
                  <span className="small faint">The rest still import</span>
                </div>
                <div className="table-wrap" style={{ maxHeight: 220 }}>
                  <table className="data">
                    <thead>
                      <tr><th className="num">Row</th><th>Name</th><th>Why</th></tr>
                    </thead>
                    <tbody>
                      {preview.failed.map((f) => (
                        <tr key={`${f.line}-${f.name}`}>
                          <td className="num">{f.line}</td>
                          <td>{f.name || <span className="faint">—</span>}</td>
                          <td>{f.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * Excel copies as tab-separated; a CSV export is comma-separated. Both arrive
 * through the same paste box, so the delimiter is worked out from the content.
 * The first row is always the header — the columns are named, not positional —
 * and each remaining row becomes an object keyed by those headers.
 */
function parse(text: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];

  const delimiter = (lines[0] ?? '').includes('\t') ? '\t' : ',';
  const split = (line: string) => line.split(delimiter).map((cell) => cell.trim());

  const headers = split(lines[0] ?? '');

  return lines.slice(1).map((line) => {
    const cells = split(line);
    const row: Record<string, string> = {};
    headers.forEach((header, i) => {
      if (header) row[header] = cells[i] ?? '';
    });
    return row;
  });
}
